import { createRouter, FileRoutesByPath, FileRouteTypes, redirect, RouterProvider, useNavigate } from '@tanstack/react-router';
import { FileRoutesByFullPath, FileRoutesById, FileRoutesByTo, routeTree } from './routeTree.gen';
import { queryClient } from '@/lib/queryClient';
import { Box, CircularProgress } from '@mui/material';
import { AuthLoader, AuthProvider, useAuth } from './lib/auth';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { LoadingScreen } from './pages/loading/Loading.component';
import GlobalStateProvider from './contexts/globalStateProvider';
import { useMemo } from 'react';
import { NotFound } from './pages/notFound/404Page.page';
import React from 'react';

export type LegacyUrlMapping = {
  newPath: FileRouteTypes['fileRoutesByTo']
  transformParams: (search: Record<string, string>) => Record<string, string | number>
  transformSearch: (search: Record<string, string>) => Record<string, string | number>
}


export type RouterContext = {
  legacyUrlMappings: Record<string, LegacyUrlMapping>
}

export const legacyUrlMappings: Record<string, LegacyUrlMapping> = {
  'ticket.html': {
    newPath: '/ticket/$ticketId',
    transformParams: (search) => ({
      ticketId: z.number().int().parse(Number(search.TicketId))
    }),
    transformSearch: () => ({
      variant: 'edit',
      step: 0
    })
  },
  'index2.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: () => ({})
  },
  'api-cfielfileupload.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'cfield-crud.go.html': {
    newPath: '/custom-fields',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'cfields-crud.html': {
    newPath: '/custom-fields',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'custom-fields-admin.html': {
    newPath: '/custom-fields',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'customer-crud.html': {
    newPath: '/customers',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'dashboard.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'field-group-admin.html': {
    newPath: '/field-groups',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'home.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'index.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'issue.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'loading.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'loadTest.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'login.html': {
    newPath: '/login',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'mass-update-execute.html': {
    newPath: '/batch-updates',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'mass-update.html': {
    newPath: '/batch-updates',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'messages.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'note-iframe.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'notification-templates.html': {
    newPath: '/templates',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'react-playground.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'release-notes.html': {
    newPath: '/release-notes',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'report.html': {
    newPath: '/reports',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'reports.html': {
    newPath: '/reports',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'reports2.html': {
    newPath: '/reports',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'roles-crud.html': {
    newPath: '/permissions',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'settings.html': {
    newPath: '/user-profile',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'smartrules-crud.html': {
    newPath: '/workflow-rules',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'smartrules-crud2.html': {
    newPath: '/workflow-rules',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'smartrules-crud3.html': {
    newPath: '/workflow-rules',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'smartrules.html': {
    newPath: '/workflow-rules',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'status.html': {
    newPath: '/status-editor',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'status-crud.html': {
    newPath: '/status-editor',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'style.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'tech-settings.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'test-loader.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'ticket-create.html': {
    newPath: '/',
    transformParams: () => ({
      drawer: 'create'
    }),
    transformSearch: (search) => search
  },
  'ticket-view-request.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'ticketnote-create.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'token-test.html': {
    newPath: '/',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'type-crud.html': {
    newPath: '/type-editor',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'type-crud2.html': {
    newPath: '/type-editor',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'user-crud.html': {
    newPath: '/users',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
  'workflow-rules.html': {
    newPath: '/workflow-rules',
    transformParams: () => ({}),
    transformSearch: (search) => search
  },
}

// Legacy URL support util

export function handleLegacyUrl(
  pathname: string,
  search: Record<string, string>,
  context: RouterContext
) {

  const legacyPath = pathname.split('/').pop()
  if (!legacyPath) return null

  // Check if this appears to be a legacy URL (ends with .html)
  const isLegacyPattern = legacyPath.endsWith('.html')
  if (!isLegacyPattern) return null

  const mapping = context.legacyUrlMappings[legacyPath]
  if (!mapping) {
    // If it looks like a legacy URL but doesn't have a mapping, redirect to home
    return redirect({
      to: '/',
      search: { drawer: undefined },
      replace: true
    })
  }

  const params = mapping.transformParams(search)
  const searchParams = mapping.transformSearch(search)

  const newPath = Object.entries(params).reduce(
    (path, [key, value]) => path.replace(`$${key}`, String(value)),
    mapping.newPath
  )

  return redirect({
    to: newPath,
    search: searchParams,
    replace: true
  })
}

export const router = createRouter({
  routeTree: routeTree,
  scrollRestoration: true,
  Wrap: ({ children }) => {
    return (
      <GlobalStateProvider>
        {children}
      </GlobalStateProvider>
    )
  },
  context: {
    auth: undefined!, // This will be set after we wrap the app in an AuthProvider
    handles: {},
    queryClient: queryClient,
    legacyUrlMappings: legacyUrlMappings
  },
  defaultPendingComponent: React.memo(() => (
    <div>
      <LoadingScreen />
    </div>
  )),
  defaultNotFoundComponent: () => (
    <div>
      <NotFound />
    </div>
  ),
  // defaultErrorComponent: () => (
  //   <div>
  //     <NotFound />
  //   </div>
  // ),
  // This make the loader only wait 200ms before showing the pending component, instead of the default 1000ms
  defaultPendingMs: 200,
  defaultPreload: 'intent',
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const InnerApp = () => {
  const auth = useAuth()
  const context = useMemo(() => ({
    auth,
    handles: {},
    queryClient,
    legacyUrlMappings
  }), [auth])

  return <RouterProvider router={router} context={context} />
}


const App = () => {

  // const navigate = useNavigate()

  return (
    <AuthLoader
      renderLoading={() => <div>Loading ...</div>}
      renderUnauthenticated={() => redirect({ to: '/login', replace: true })}
    >
      <AuthProvider>
        <InnerApp />
      </AuthProvider>
    </AuthLoader>
  )
};

export default App;
